import { GetStaticPaths, GetStaticProps } from "next";
import Image from 'next/image';
import { NextSeo } from "next-seo";

import Category from "../components/Category";
import GraftCalculator from '../components/GraftCalculator';
import HairTransplantPrices from '../components/hair-transplant-prices';
import Layout from "../components/Layout";
import Page from "../components/Page";
import Treatment from "../components/Treatment";
import {
  CategoryFragment,
  HairTransplantPackage,
  PageFragment,
  Stage,
  TreatmentFragment,
} from "../interfaces";
import baseUrl from "../lib/baseUrl";
import getClient from "../lib/getClient";
import getCommonProps, { CommonProps } from "../lib/getCommonProps";

// Add this testimonials array at the top of your file
const testimonials = [
  {
    text: "Extremely friendly and professional from start to finish. I would recommend the welfare abroad team to anyone thinking of having a hair transplant as it is daunting having a procedure in a different country. The care is absolutely first...",
    name: "Ian W.",
    rating: 5,
    date: "2 years ago"
  },
  {
    text: "Just arrived back in the UK after my hair transplant. I was very happy with the whole trip from airport transfers, hotel transfers to and from the hospital and the procedure itself. I would recommend welfare abroad to anyone thinking about getting a transplant, a special thanks to Ulash, Dr Gamze, Richard and the technicians.",
    name: "Matty Lawrenson",
    rating: 5,
    date: "a year ago"
  },
  {
    text: "Very pleased with the whole experience, from airport pick up, the whole team very friendly and professional. Had my reservations going abroad for surgery, without hesitation would recommend. A fraction of the price of UK, modern clinic and fully regulated, thank you again!",
    name: "Mark Sinclair",
    rating: 5,
    date: "a year ago"
  },
  {
    text: "It's not often I write reviews but I feel like this experience is worth giving people an insight to what welfare abroad are about and for anyone wishing to get a hair transplant. The service & communication from the start to sending your...",
    name: "Joshua Ward",
    rating: 5,
    date: "11 months ago"
  }
];

const TestimonialsSection = () => (
  <div className="py-32 bg-gray-50">
    <div className="container mx-auto px-4">
      <h2 className="text-5xl font-bold text-center mb-16 text-gray-800">What Our Clients Say</h2>
      <div className="grid md:grid-cols-2 gap-8">
        {testimonials.map((testimonial, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
            <div className="flex items-center mb-4">
              <div className="text-yellow-400 mr-2">
                {"★".repeat(testimonial.rating)}
              </div>
              <span className="text-gray-600 text-sm">{testimonial.date}</span>
            </div>
            <p className="text-gray-600 mb-4">&quot;{testimonial.text}&quot;</p>
            <p className="font-bold text-blue-600">{testimonial.name}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

type Props = CommonProps & {
  data: (PageFragment | TreatmentFragment | CategoryFragment) | {
    __typename: "HairTransplantPrices";
    seo: { slug: string };
    hairTransplantPackages: HairTransplantPackage[];
  };
};

const CatchAll = ({ data, ...commonProps }: Props) => {
  const renderSwitch = (data: Props["data"]) => {
    if (data.__typename === "HairTransplantPrices") {
      return (
        <>
          <NextSeo
            title="Hair Transplant Packages | Welfare Abroad"
            description="Explore our hair transplant package options in Turkey and UK. Calculate your graft needs and find the perfect solution for you."
            canonical={baseUrl + "/hair-transplant-prices"}
          />
          
          {/* Hero Section */}
          <div className="relative bg-gradient-to-r from-blue-700 to-blue-500 text-white py-24">
            <div className="container mx-auto px-4 relative z-10">
              <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">Premium Hair Transplants at Affordable Prices</h1>
              <p className="text-xl md:text-2xl mb-10 font-light">Experience world-class care in Turkey or the UK with Welfare Abroad</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#calculator" className="bg-green-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-green-600 transition duration-300 shadow-lg text-lg text-center">Calculate Your Graft Needs</a>
                <a href="#packages" className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-700 transition duration-300 shadow-lg text-lg text-center">Our Hair Transplant Packages</a>
              </div>
            </div>
            <div className="absolute inset-0 bg-black opacity-40"></div>
            <Image src="/istOrlondon.jpeg" layout="fill" objectFit="cover" alt="Hair Transplant Hero" className="absolute inset-0" />
          </div>

          {/* New Transparent Pricing Section */}
          <div className="py-32 bg-white">
            <div className="container mx-auto px-4">
              <h2 className="text-5xl font-bold text-center mb-12 text-gray-800">Transparent Pricing for Your Hair Transplant Journey</h2>
              <div className="max-w-3xl mx-auto text-gray-600 space-y-8 text-lg">
                <p>
                  At Welfare Abroad, we believe in offering clear and comprehensive pricing for your hair transplant procedures. We understand that each person&apos;s hair restoration journey is unique, and it&apos;s crucial to have full visibility into what&apos;s included in your treatment package. Whether you&apos;re exploring your options for the first time or comparing services, our packages are designed to offer exceptional value, covering all essential care and services to ensure a successful outcome.
                </p>
                <p>
                  Our packages provide a holistic approach to your hair transplant experience. From expert consultation and personalized treatment plans to advanced, state-of-the-art facilities and techniques, we ensure every aspect of your procedure is carefully managed. You&apos;ll receive comprehensive post-operative care and follow-up services to support your recovery, all at an affordable price with no hidden fees.
                </p>
                <p>
                  With our commitment to excellence, you can rest assured that your experience will be seamless, tailored to your individual needs, and focused on delivering the best possible results.
                </p>
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div id="packages" className="py-32 bg-gray-50">
            <div className="container mx-auto px-4">
              <h2 className="text-5xl font-bold text-center mb-16 text-gray-800">Our Hair Transplant Packages</h2>
              <HairTransplantPrices packages={data.hairTransplantPackages} />
              <div className="mt-16 text-center">
                <a href="#calculator" className="bg-green-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-green-600 transition duration-300 shadow-lg text-lg inline-block">Calculate Your Graft Needs</a>
              </div>
            </div>
          </div>

          {/* Calculator Section */}
          <div id="calculator" className="py-32 bg-white">
            <div className="container mx-auto px-4">
              <GraftCalculator />
            </div>
          </div>

          {/* Testimonials Section */}
          <TestimonialsSection />
        </>
      );
    }

    switch (data?.__typename) {
      case "Page":
        return <Page page={data} />;
      case "Treatment":
        return <Treatment {...data} />;
      case "Category":
        return (
          <Category
            category={data}
            posts={data.posts}
            treatments={data.treatments}
          />
        );
      default:
        return;
    }
  };

  return (
    <Layout {...commonProps}>
      {renderSwitch(data)}
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const commonProps = await getCommonProps(context);
  const sdk = await getClient(context.preview);
  const tcSlugs = await sdk.TreatmentCategoryIdSlugs();
  const { pages: pageSlugs } = await sdk.PageSlugs();
  const slugs = [...tcSlugs.treatments, ...tcSlugs.categories, ...pageSlugs];
  const currentSlug = context.params?.slug?.slice(-1)[0];
  
  if (currentSlug === 'hair-transplant-prices') {
    try {
      const { hairTransplantPackages } = await sdk.HairTransplantPrices();
      return {
        props: {
          data: {
            __typename: "HairTransplantPrices",
            seo: { slug: 'hair-transplant-prices' },
            hairTransplantPackages,
          },
          preview: context.preview || null,
          ...commonProps,
        },
        revalidate: 60,
      };
    } catch (error) {
      console.error("Error fetching hair transplant packages:", error);
      return {
        props: {
          data: {
            __typename: "HairTransplantPrices",
            seo: { slug: 'hair-transplant-prices' },
            hairTransplantPackages: [],
          },
          preview: context.preview || null,
          ...commonProps,
        },
        revalidate: 60,
      };
    }
  }

  const id = slugs.find((tc) => tc.seo.slug === currentSlug)?.id;

  if (!id) {
    return {
      notFound: true,
    };
  }

  const { treatment, category } = await sdk.TreatmentCategoryById({
    id: id,
    stage: context.preview ? Stage.Draft : Stage.Published,
  });
  const { page } = await sdk.PageById({
    id: id,
    stage: context.preview ? Stage.Draft : Stage.Published,
  });
  const data = page || treatment || category;

  return {
    props: {
      data,
      preview: context.preview || null,
      ...commonProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const sdk = await getClient();
  const { treatments } = await sdk.TreatmentCategoryIdSlugs();

  const treatmentPaths = treatments.map((item) => ({
    params: {
      slug: [
        ...item.categories.map((category) => category.seo?.slug),
        item.seo?.slug,
      ],
    },
  }));

  // Remove last element to get the category paths
  const pathWithRoot = [
    ...treatmentPaths,
    ...treatmentPaths.map((path) => ({
      params: { slug: path.params.slug.slice(0, -1) },
    })),
  ];

  // Remove root element
  const paths = pathWithRoot.filter((paths) => paths.params.slug.length > 0);

  const { pages } = await sdk.PageSlugs();

  return {
    paths: [
      ...paths,
      ...pages.map((page) => ({ params: { slug: [page.seo.slug] } })),
      { params: { slug: ['hair-transplant-prices'] } },
    ],
    fallback: false,
  };
};

export default CatchAll;
